import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend,  Label } from 'recharts';
import moment from 'moment';
import '../../stations/Station.scss';
import { ONEDAY, getMaxFromSelectedFields, getMinFromSelectedFields } from 'src/store/common';
import { useStoreContext } from 'src/store/context';

type DoubleVarsProps  = {
    all_dates: any[],
    type: string
}
export const CustomTooltip:any = (props:any) => {
  const {active, label,payload, timezoneOffset, type, color1, color2}=props;
  if (active && payload?.length && typeof label!=='undefined') {
    return (
      <div id="custom-tooltip" className="custom-tooltip" style={{backgroundColor: 'white', border: 'none !important', display:'block', height: '4rem', width: '5rem'}}>
        <span className="month" style={{fontSize: '.75rem', paddingBottom: '0px', paddingTop:'0px', display:'block'}}>
          {type==='hourly' ? (moment(label).utc().utcOffset(timezoneOffset).format("MMM DD, YYYY")): (moment(label).format("MMM DD, YYYY"))}
        </span>
        <span className="hours" style={{fontSize:'.75rem', paddingTop: '0px', paddingBottom: '0px', display:'block'}}>
        {type==='hourly' && moment(label).utc().utcOffset(timezoneOffset).format("HH:mm")}
        </span>
        <svg width="50" height="11" style={{display: 'block', paddingTop: '0px', paddingBottom: '0px'}}>
          <rect width="5" height="5" style={{fill:color1}}/>
          <text
            fontSize=".75rem"
            height="5"
            x={10}
            y={10}
          >
          {payload[0] && payload[0].value && `${payload[0].value}`}
        </text>
        </svg>
        <svg width="50" height="11" style={{display: 'block',paddingTop:'0.5px' }}>
          <rect width="5" height="5" style={{fill:color2}}/>
          <text
            fontSize=".75rem"
            height="8"
            x={10}
            y={10}
          >
          {payload[1] && payload[1].value && `${payload[1].value}`}
        </text>
        </svg>
      </div>
    );
  }
  return null;
};

export const CustomizedAxisTick: any = (props: any) => {
  const { x, y, payload,timezoneOffset, type } = props;
  if(payload?.value){
    return (
      <g transform={`translate(${x},${y})`} >
        <text
          fontSize=".75rem"
          x={0}
          y={2}
          dy={16}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)"
        >
          {type==='hourly'? moment(payload.value).utc().utcOffset(timezoneOffset).format('MMM DD'):moment(payload.value).format('MMM DD')}
        </text>
      </g>
    );
  }
};

export const DoubleVars: React.FC<DoubleVarsProps> = ({
  all_dates, type
}) => {
  const [xTicks, setXTicks] = useState<number[]>([]);
  const [state] = useStoreContext();

  const findDomain = () =>{
    const min = Number(getMinFromSelectedFields(all_dates, [`${state.chartTable.dataKeyO1}`, `${state.chartTable.dataKeyF1}`,`${state.chartTable.dataKeyO2}`,`${state.chartTable.dataKeyF2}`], 5).toFixed(1));
    const max = Number(getMaxFromSelectedFields(all_dates, [`${state.chartTable.dataKeyO1}`, `${state.chartTable.dataKeyF1}`,`${state.chartTable.dataKeyO2}`,`${state.chartTable.dataKeyF2}`]).toFixed(1));

    if(Number.isNaN(min)){
      return([0,1])
    }
    else{
      return[0, max];
    }
  }

  useEffect(()=>{
    if(all_dates.length>1){
      let ticks:number[]=[];

      if(ticks.length<1){
        if(type==='hourly'){
          let i = moment(all_dates[0].date).utc().utcOffset(all_dates[0].offset).format('MMM DD');
          ticks.push(moment(all_dates[0].date).set('hour', 13).utc().utcOffset(all_dates[0].offset).valueOf())
          let j=0;
          while(i !== moment(all_dates.at(-1).date).format('MMM DD')){          
            j=j+1;
            ticks.push(moment(all_dates[0].date - j*ONEDAY).set('hour', 13).utc().utcOffset(all_dates[0].offset).valueOf())
            i = moment(all_dates[0].date - j*ONEDAY).utc().utcOffset(all_dates[0].offset).format('MMM DD');
          }
          setXTicks(ticks)
        }
        else{
          let i = moment(all_dates[0].date).format('MMM DD');
          ticks.push(all_dates[0].date)
          let j = 0;
          while (i !== moment(all_dates.at(-1).date).format('MMM DD')) {
            j = j + 1;
            ticks.push(all_dates[0].date - j * ONEDAY)
            i = moment(all_dates[0].date - j * ONEDAY).utc().utcOffset(all_dates[0].offset).format('MMM DD');
          }
          setXTicks(ticks)
        }
      }
  
    }
  },[all_dates, type]);

  return(
    <>
    {all_dates.length>1 &&(
        <LineChart
          width={425}
          height={190}
          data={all_dates}
          margin={{
            top: (10),
            right: 10,
            left: 5,
            bottom: 10,
          }}
        >
          <XAxis dataKey="date" tick={<CustomizedAxisTick timezoneOffset={all_dates[0].offset} type={type}/>} interval={0} type="number" domain={[all_dates.at(-1).date, all_dates[0].date]} ticks={xTicks}/>          
          <YAxis yAxisId="left" domain={findDomain()} 
            style={{fontSize: '0.7rem',fontFamily: 'serif'}} axisLine={false}>
          <Label
            value={`${state.chartTable.yAxis}`}
            position="insideLeft"
            angle={-90}
            fontSize={'0.7rem'}
            style={{ textAnchor: 'middle'}}
          />
          </YAxis>
          <Tooltip content={<CustomTooltip timezoneOffset={all_dates[0].offset} type={type} color1={`${state.chartTable.color1}`} color2={`${state.chartTable.color2}`}/>} position={{ y: -5 }} />
          <Legend wrapperStyle={{fontSize:'.7rem', top:'180px'}} iconType="plainline" iconSize={10}/>          
          <Line name={`${state.chartTable.dataLabel1}`} type="monotone" yAxisId ="left" dataKey={`${state.chartTable.dataKeyO1}`} stroke={`${state.chartTable.color1}`} dot={ false} strokeWidth={2}/>
          <Line name={`${state.chartTable.dataLabel1} Forecast`} type="monotone" yAxisId = "left" dataKey={`${state.chartTable.dataKeyF1}`} stroke={`${state.chartTable.color1}`} dot={false} strokeDasharray="5 5" strokeWidth={2}/>
          <Line name={`${state.chartTable.dataLabel2}`} type="monotone" yAxisId ="left" dataKey={`${state.chartTable.dataKeyO2}`} stroke={`${state.chartTable.color2}`} dot={ false} strokeWidth={2}/>
          <Line name={`${state.chartTable.dataLabel2} Forecast`} type="monotone" yAxisId = "left" dataKey={`${state.chartTable.dataKeyF2}`} stroke={`${state.chartTable.color2}`} dot={false} strokeDasharray="5 5" strokeWidth={2}/>
        </LineChart>
    )}
    </>

  );
}